import { useState } from 'react';
import { findCurrentSlide } from './utils/find-current-slide';
import { wizardReducer } from './use-wizard-reducer';
import { WizardState, WizardAction } from './utils/wizard-types';
import { SectionKey } from 'types';

export const useWizard = (
  reducer: typeof wizardReducer,
  sectionData: PvWizardSections,
  sectionOrder: SectionKey[],
  searchParams: PvWizardResultsQueryVariables
): [WizardState, (action: WizardAction) => void] => {
  const { initialSectionOrderIndex, initialSlideKey, initialActiveSlideIndex } = findCurrentSlide(
    searchParams,
    sectionOrder,
    sectionData
  );
  const initialActiveWizardStep = sectionOrder[initialSectionOrderIndex];
  const initialActiveSlide = initialSlideKey;
  const isLastWizardStep = initialActiveWizardStep === 'solarStorage';
  const isLastQuestion = initialActiveSlide === 'roofDetails';
  const isFirstWizardStep = Boolean(
    initialActiveSlide === 'houseType' && !Object.keys(searchParams).length
  );
  const isWizardClickedThrough =
    initialActiveSlideIndex < 0 && typeof initialActiveSlide === 'number';

  const initialState: WizardState = {
    isLastQuestion: isLastQuestion || isWizardClickedThrough,
    isLastStep: isLastWizardStep || isWizardClickedThrough,
    isFirstStep: isFirstWizardStep && !isWizardClickedThrough,
    answers: searchParams,
    activeSlide: initialActiveSlideIndex,
    activeWizardStep: initialActiveWizardStep,
    activeSlideKey: initialActiveSlide,
    isWizardClickedThrough
  };

  const [state, setState] = useState(initialState);

  const dispatch = (action: WizardAction) => {
    const updatedState = {
      ...state,
      isWizardClickedThrough: action.isWizardClickedThrough ?? state.isWizardClickedThrough
    };

    const nextState = reducer(updatedState, action, sectionData, sectionOrder);
    setState(nextState);
  };

  return [state, dispatch];
};
